/* The preview column — KANON `Cwiczenia` 17.09.2026, variant e, with the owner's last-mile change.
 *
 * 396px, the clients roster's own anatomy: an identity row that is not a card, then cards with the
 * canon's 12px between them. The column is the pane's height, and the toolbar and the column heads
 * beside it never leave the screen.
 *
 * ── D18: the three cards FIT the window, and the panel scrolls only past the floor ───────────
 * Owner, 18 September 2026, from a 1440×810 window where the equipment card stood cut off under
 * the bottom edge: the panel is to hold all of its content without scrolling, and the photograph
 * is never to be cropped. The column is a size container, and the WINDOW alone decides how much
 * the two tall boxes give: the shortfall of the column's height against the canon's 761px
 * (`--h-ex-panel-canon`) is taken by the figure box first, down to its floor, then by the still,
 * which keeps the canon frame's proportion and narrows rather than being cut. The equipment card,
 * the identity row and every pill keep their size; at the reference 1512×1000 the shortfall is
 * nought and nothing moves. A first cut let the cards FLEX against their content instead, and the
 * movement sampler caught what that means: a movement with three rows of pills or two of
 * equipment made the loaded column taller than the pending one, and the still shrank on the frame
 * the answer landed. So data never enters the sum — more pills or tiles than the canon's row grow
 * into the column's scroll exactly as they did before — and only when both floors are reached
 * does `overflow: auto` take over, which is the fit-or-scroll rule's last resort and never a clip.
 *
 * ── D3: the identity row shows the NAME, and the still lives in its own card below ───────────
 * The owner's change to the specimen before the canon was frozen. So there is no photograph in the
 * head — a name, the type's mark and the „Twoje" badge — and the first card is the still.
 *
 * ── The reserves, and which shape each one holds ────────────────────────────────────────────
 * `shared/AGENTS.md`'s rule: a card with something UNDER it reserves its TALLER loaded shape,
 * because growth pushes what is below; a card with nothing under it reserves the SHORTER, because a
 * collapse leaves a hole. Here the still and the muscles both have a card under them and hold their
 * full height — the skeleton's bars are `flex` under the same basis, floor and cap as the loaded
 * boxes, so the pending column at any window height is the loaded column's geometry; the equipment
 * card is LAST and holds one row of tiles, growing downward into the column's own scroll when a
 * movement names more.
 *
 * Every text leaf states its line-height. Heights and widths are layout rather than design tokens;
 * every colour, space, radius and type value below is a token. */

.panel {
  --sb-size: 10px;
  --sb-radius: var(--radius-xs);

  /* The shortfall against the canon column, and how the two tall boxes share it — read by the
   * still and by the figures below through `cqh`, which resolves against THIS box because it is
   * the nearest size container. `max()` keeps a tall window at nought. */
  --ex-deficit: max(0px, calc(var(--h-ex-panel-canon) - 100cqh));
  --ex-give-figures: min(var(--ex-deficit), calc(var(--h-ex-figure-box) - var(--h-ex-figure-box-min)));
  --ex-give-still: min(
    calc(var(--ex-deficit) - var(--ex-give-figures)),
    calc(var(--h-ex-still) - var(--h-ex-still-min))
  );

  container-type: size;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: var(--space-3);
  width: 396px;
  min-height: 0;
  overflow: hidden auto;
  scrollbar-gutter: stable;
}

/* The identity row. `min-height` rather than a height, so a two-line name grows the row instead of
 * clipping — and a reserve, so the card under it does not move on the frame the name arrives. */
.identity {
  display: flex;
  flex: none;
  align-items: center;
  min-height: 48px;
  padding: var(--space-2px) var(--space-2px) var(--space-4px);
}

.name {
  margin: var(--space-0);
  font-size: var(--fs-detail-name);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-display);
  letter-spacing: var(--ls-heading);
  color: var(--text);
}

.sub {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  margin-top: var(--space-4px);
}

/* The type as a dot and its WORD — the panel has room for the word the table's 56px column does
 * not, which is the whole reason the glyph is a glyph out there. */
.typeMark {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-block);
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--type-ink);
}

.typeGlyph {
  display: block;
  flex: none;
  width: 13px;
  height: 13px;
  color: var(--type-ink);
}

.badge {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: 18px;
  padding: var(--space-0) var(--space-7px);
  font-size: var(--fs-badge);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-cat);
  white-space: nowrap;
  color: var(--text);
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-xs);
}

.card {
  flex: none;
  padding: var(--space-13px) var(--space-15px);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-12px);
  box-shadow: var(--shadow-card);
}

.eyebrow {
  display: block;
  margin: var(--space-0) var(--space-0) var(--space-10px);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-group);
  text-transform: uppercase;

  /* `--text-2`, not `--text-3`: measured at 3.01:1 in Flame, below the 4.5:1 a readable run
   * owes. Owner decision, 17 September 2026 — deviations register entry 18. */
  color: var(--text-2);
}

/* The still's card has no eyebrow — the picture is self-evident — so it is inset by its own edge
 * and nothing else.
 *
 * `min-height` on BOTH tall cards is the reserve the gate cannot take away: for the first 150ms the
 * skeleton mounts no bars at all, and a card that is only its padding then is 28px where 234 will
 * stand — the movement sampler read the muscle card jumping 206px on the frame the bars arrived.
 * The reserve is the loaded height less the same give the loaded box takes, so it holds at every
 * window height. */
.stillCard {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: calc(var(--h-ex-still) - var(--ex-give-still) + var(--h-ex-still-chrome));
  padding: var(--space-13px) var(--space-15px);
}

/* GEOMETRY only — the frame's treatment is `exerciseStill.module.css`'s. Nothing on both sides.
 * The glyph is resized through that sheet's own hook: 27% at 366px reads as the table's 45% does at
 * 44px. */
.still {
  --ex-still-glyph: 27%;

  width: 100%;
  border-radius: var(--radius-mark);
}

/* The muscle card. Its reserve is the TALLER shape — the equipment card sits under it — and on a
 * short window its figure box gives height, down to its floor, before the still gives any.
 *
 * The give is read HERE, one level above the box, because a custom property cannot be redefined
 * in terms of itself on the same element: the box and the figure below are the same two tokens
 * `MuscleFigures` already draws by, each less the give, so the caption keeps its room. */
.muscleCard {
  --ex-figure-box-given: calc(var(--h-ex-figure-box) - var(--ex-give-figures));
  --ex-figure-given: calc(var(--h-ex-figure) - var(--ex-give-figures));

  min-height: calc(var(--ex-figure-box-given) + var(--h-ex-muscle-chrome));
}

.figures,
.skelFigures {
  --h-ex-figure-box: var(--ex-figure-box-given);
  --h-ex-figure: var(--ex-figure-given);

  display: flex;
  justify-content: center;
}

.pills {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-5px);
  margin-top: var(--space-10px);
}

.pill {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: var(--space-0) var(--space-10px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  white-space: nowrap;
  color: var(--text);
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-8px);
}

.pillSecondary {
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  background: transparent;
}

.empty {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-3);
}

/* ── The skeleton ────────────────────────────────────────────────────────────────────────── */

/* The column stands from the FIRST frame, in every state. The gate hides the BARS, never the box,
 * so the movement landing changes ink and never geometry. */
.skelName {
  width: 62%;
  height: 15px;
}

.skelSub {
  width: 38%;
  height: 10px;
  margin-top: var(--space-6px);
}

.skelStill {
  width: 100%;
}

/* The still's box is the canon's 206px less the window's give, and it keeps the canon frame's own
 * proportion — 364 by 206 — so a shorter box is a NARROWER box, centred in the card, and the
 * photograph inside is shown whole rather than cropped (the owner's ask, 18 September 2026). At
 * the reference the width is capped by the card and the frame is exactly the canon's.
 * `.stillCard .still` rather than `.still` so it outranks the frame's own geometry whatever order
 * the two sheets load in. */
.stillCard .still,
.stillCard .skelStill {
  width: auto;
  max-width: 100%;
  height: calc(var(--h-ex-still) - var(--ex-give-still));
  aspect-ratio: 364 / 206;
}

.skelFigure {
  width: 84px;
  height: var(--h-ex-figure);
}

.skelPill {
  height: 24px;
}

.skelPills .skelPill:nth-child(1) {
  width: 96px;
}

.skelPills .skelPill:nth-child(2) {
  width: 72px;
}

.skelFigures {
  gap: var(--space-9-5px);
}
